import { useEffect, useState } from 'react';
import {
  listenToDirectory, listenToPendingRequests,
  type DirectoryUser, type MuqriRequest,
} from '@core/firebase/admin';
import { listenToOpenSessions, type OpenSession } from '@core/firebase/sessions';
import { summarize } from '@core/admin/stats';
import { useLanguage } from '@/contexts/LanguageContext';
import { PageHeader } from '@/components/PageKit';

/**
 * The dashboard's numbers, derived rather than stored.
 *
 * Counted from the SAME live collections the other tabs read, through the
 * shared summarizer, so a figure here can never disagree with the list one
 * tab over. A stored counter would be a second source of truth that drifts
 * the first time a write fails halfway.
 */
export default function StatsPage() {
  const { t } = useLanguage();
  const [users, setUsers] = useState<DirectoryUser[]>([]);
  const [requests, setRequests] = useState<MuqriRequest[]>([]);
  const [sessions, setSessions] = useState<OpenSession[]>([]);

  useEffect(() => listenToDirectory(setUsers), []);
  useEffect(() => listenToPendingRequests(setRequests), []);
  useEffect(() => listenToOpenSessions(setSessions), []);

  const stats = summarize(users, requests, sessions);
  const count = (n: number) => new Intl.NumberFormat('en-US').format(n);

  const tiles: { testId: string; labelKey: string; value: number }[] = [
    { testId: 'stat-users', labelKey: 'admin.stats.users', value: stats.users },
    { testId: 'stat-muqris', labelKey: 'admin.stats.muqris', value: stats.muqris },
    { testId: 'stat-admins', labelKey: 'admin.stats.admins', value: stats.admins },
    { testId: 'stat-pending', labelKey: 'admin.stats.pending', value: stats.pendingRequests },
    { testId: 'stat-live', labelKey: 'admin.stats.live', value: stats.liveSessions },
    { testId: 'stat-waiting', labelKey: 'admin.stats.waiting', value: stats.studentsWaiting },
  ];

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      {/* Board a8: every number says its period. These are live counts, so
          the period is «الآن», said once in the header and on every tile. */}
      <PageHeader title={t('admin.stats.title')} subtitle={t('admin.stats.periodNote')} />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        {tiles.map(({ testId, labelKey, value }) => (
          <div key={testId} className="card-soft p-5">
            <p data-testid={testId} className="text-[32px] font-semibold leading-none text-ink">
              {count(value)}
            </p>
            <p className="mt-3 text-[15px] text-ink">{t(labelKey)}</p>
            <p className="mt-1 flex items-center gap-1.5 text-xs text-helper">
              <span className="h-2 w-2 rounded-full bg-lime" />
              {t('admin.stats.period')}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
